import type { EntityOption } from '@/components/EntitySelect';
import { listMembers, type MemberListParams } from './api';
import type { Member } from './types';

/** Display label for a member in a picker: name, with a tag for guests. */
function memberLabel(member: Member): string {
  const name = member.user.full_name;
  return member.type === 'guest' ? `${name} (guest)` : name;
}

/**
 * Owner/assignment fields on the backend are FKs to `users.User` (e.g.
 * `crm.Deal.owner`), so these pickers key on the **user id**, while sourcing
 * the candidate list from the org-scoped members endpoint.
 */
export async function searchMemberUsers(
  query: string,
  params: Omit<MemberListParams, 'search' | 'user'> = {},
): Promise<EntityOption[]> {
  const page = await listMembers({ search: query || undefined, ...params });
  return page.results.map((m) => ({ value: String(m.user.id), label: memberLabel(m) }));
}

/** Resolve a user's display name for a preselected owner id (edit mode). Includes
 *  terminated members so an already-assigned (now-terminated) owner still shows a
 *  label — they're only hidden from the list of *new* choices. */
export async function resolveMemberUserLabel(value: string): Promise<string | null> {
  if (!value) return null;
  const page = await listMembers({ user: Number(value), include_inactive: true });
  const member = page.results[0];
  return member ? member.user.full_name : null;
}
